import Vue from 'vue'
import { msalInstance } from './auth/msalService'
import { ensureConfigReady } from '@/plugins/configs'

Vue.config.productionTip = false
Vue.config.silent = true
window.authReady = false
window.loginInProgress = true
window.appReady = false

async function getDashboardVersion(baseUrl = window.location.origin) {
  try {
    const url = `${baseUrl}/version.json?t=${Date.now()}`
    const res = await fetch(url, { cache: 'no-cache' })
    if (!res.ok) throw new Error(`Failed to load version.json`)
    const data = await res.json()
    return data?.dashboardVersion || ''
  } catch (e) {
    console.warn('Version fetch failed:', e)
    return ''
  }
}


async function preloadUpdatedChunks() {
  // Skip version check if MSAL redirect is in progress
  if (window.location.href.includes('code=') && window.location.href.includes('state=')) {
    console.log('🔄 Skipping version check during MSAL redirect')
    return true
  }
}

async function checkVersionBeforeLoad() {
  const currentVersion = await getDashboardVersion()
  const storedVersion = localStorage.getItem('uiVersion')

  console.log('Version check:', { currentVersion, storedVersion })

  // Prevent infinite reload loop
  if (sessionStorage.getItem('versionReloading')) {
    sessionStorage.removeItem('versionReloading')
    return true
  }

  if (!storedVersion) {
    localStorage.setItem('uiVersion', currentVersion)
    return true
  }

  if (storedVersion !== currentVersion) {
    console.log('🆕 Detected new version:', currentVersion)

    // 🧩 Preload the new build so browser caches it
    await preloadUpdatedChunks()

    // Mark reload once
    sessionStorage.setItem('versionReloading', 'true')
    localStorage.setItem('uiVersion', currentVersion)

    // Reload to mount from cached new assets
    window.location.reload(true)
    return false
  }

  return true
}

// 🔄 Global ChunkLoadError handler — auto-reload when stale chunks fail after a new deployment
window.addEventListener('error', (event) => {
  const err = event.error || event
  if (err && err.name === 'ChunkLoadError') {
    console.warn('⚠️ ChunkLoadError detected — new deployment likely. Reloading...')
    if (!sessionStorage.getItem('chunkErrorReloading')) {
      sessionStorage.setItem('chunkErrorReloading', 'true')
      window.location.reload(true)
    }
  }
})

async function init() {
  await ensureConfigReady()

  // 🔑 Save email redirect data BEFORE any MSAL processing or version check
  // Email links contain ?redirect=true&data=... which must be persisted
  // before MSAL's handleRedirectPromise() navigates away
  const params = new URL(window.location.href).searchParams
  if (params.get('redirect')) {
    localStorage.setItem('redirectURL', window.location.pathname)
    localStorage.setItem('redirect', 'true')
    localStorage.setItem('redirectData', params.get('data'))
  }

  // Clear any previous chunk-error reload flag at startup
  sessionStorage.removeItem('chunkErrorReloading')

  const ok = await checkVersionBeforeLoad()
  if (!ok) return

  try {
    await msalInstance.initialize()
    await msalInstance.handleRedirectPromise()

    window.loginInProgress = false
    window.authReady = true
    console.log('✅ MSAL initialized, loading app...')

    // Load tooltip early (before app mounts) - it's needed for initial render
    await Promise.all([
      import('vue-directive-tooltip').then(module => {
        Vue.use(module.default || module)
      }),
      import('vue-directive-tooltip/dist/vueDirectiveTooltip.css')
    ])

    const { loadFullApp } = await import('@/app-init')
    await loadFullApp(msalInstance)
  } catch (err) {
    // 🔄 If initial app load itself hits a ChunkLoadError, reload once
    if (err && err.name === 'ChunkLoadError') {
      console.warn('⚠️ ChunkLoadError on init — reloading for fresh chunks...')
      if (!sessionStorage.getItem('chunkErrorReloading')) {
        sessionStorage.setItem('chunkErrorReloading', 'true')
        window.location.reload(true)
        return
      }
    }
    console.error('❌ Init error:', err)
  }
}

init()